import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  ParkedOrderListItem,
  ParkedOrderSnapshot,
} from '@matix/types';

/**
 * Hybrid local-first store for parked orders.
 *
 * Design:
 *
 *   The list of parked orders the cashier sees is the **union** of:
 *   - rows the cashier just parked locally (visible instantly even before
 *     the server confirms — that's the "instant UX" guarantee)
 *   - rows the server returned the last time we asked (gives us
 *     persistence across logout, refresh, multi-station)
 *
 *   On every visible state change we write the merged list to localStorage
 *   (Zustand persist middleware) so a hard reload loses nothing. On boot
 *   we kick off `refresh()` which calls GET /parked-orders and reconciles.
 *
 *   Sync rules:
 *     - "pending" entries are local-only POSTs that have not yet been
 *       acknowledged by the server. They live by their client-generated
 *       UUID until the POST succeeds, at which point the entry is
 *       reconciled with its server id.
 *     - "synced" entries are mirrored from the server's list. They are
 *       authoritative — if the server doesn't have a row, it gets removed
 *       from the local view on the next refresh (someone else, perhaps a
 *       parallel tab, deleted it).
 *
 * What this store does NOT own:
 *   - The cart itself. Recall reads from this store and writes to the
 *     cart store (apps/cashier/src/store/cart.ts). Park reads from cart
 *     and writes here.
 *   - The actual HTTP calls. The store calls into a thin "api adapter"
 *     passed at boot time, so this file stays testable and dep-light.
 */

/** A row as it appears in the UI: server-synced OR locally pending. */
export type ParkedOrderRow =
  | (ParkedOrderListItem & { status: 'synced' })
  | {
      id: string;            // client-generated UUID, replaced on sync
      customer_label: string | null;
      item_count: number;
      total_value: number;
      note: string | null;
      parked_at: string;
      status: 'pending' | 'failed';
      /** Snapshot kept local until POST confirms — needed for retry + recall-while-pending. */
      snapshot: ParkedOrderSnapshot;
      /** Idempotency key used on POST; persists across retries. */
      idempotencyKey: string;
    };

/** Adapter the store calls into for HTTP. Injected at app boot. */
export interface ParkedOrdersApiAdapter {
  list: () => Promise<ParkedOrderListItem[]>;
  create: (input: {
    idempotency_key: string;
    snapshot: ParkedOrderSnapshot;
    note: string | null;
  }) => Promise<{ id: string; parked_at: string; replayed: boolean }>;
  getById: (id: string) => Promise<{ snapshot: ParkedOrderSnapshot } & ParkedOrderListItem>;
  deleteById: (id: string) => Promise<void>;
}

interface ParkedOrdersState {
  /** All visible parked orders, newest first. */
  rows: ParkedOrderRow[];
  /** True while the boot-time refresh() is in flight. */
  loading: boolean;
  /** Last successful server refresh time (UTC ms); 0 if never. */
  lastSyncedAt: number;

  // ── Actions ──────────────────────────────────────────────────────────
  setApi: (api: ParkedOrdersApiAdapter) => void;
  refresh: () => Promise<void>;
  park: (input: {
    snapshot: ParkedOrderSnapshot;
    note?: string | null;
    customerLabel: string | null;
    itemCount: number;
    totalValue: number;
  }) => Promise<void>;
  recall: (id: string) => Promise<ParkedOrderSnapshot | null>;
  remove: (id: string) => Promise<void>;
  /**
   * Called by Recall+Charge success: the parked row is consumed by the
   * order. Removes it locally and from the server.
   */
  consume: (id: string) => Promise<void>;
}

let api: ParkedOrdersApiAdapter | null = null;

export const useParkedOrdersStore = create<ParkedOrdersState>()(
  persist(
    (set, get) => ({
      rows: [],
      loading: false,
      lastSyncedAt: 0,

      setApi(next) {
        api = next;
      },

      async refresh() {
        if (!api) return;
        set({ loading: true });
        try {
          const remote = await api.list();
          const remoteIds = new Set(remote.map((r) => r.id));

          // Keep local pending/failed rows that the server doesn't know
          // about yet. Drop synced rows that the server no longer has
          // (deleted from another tab/station). Replace synced rows with
          // the freshly fetched ones (truth = server).
          const remaining = get().rows.filter(
            (row) => row.status !== 'synced',
          );

          const synced: ParkedOrderRow[] = remote.map((r) => ({
            ...r,
            status: 'synced',
          }));

          // Pending rows whose key matches a server id (because the POST
          // just landed during refresh) are de-duplicated by id.
          const merged = [...synced, ...remaining].filter((row, i, arr) => {
            if (row.status !== 'synced') return true;
            return arr.findIndex((r) => r.id === row.id) === i;
          });

          // Sort newest first — server already does this for synced rows,
          // but pending rows (from optimistic park) need to slot in.
          merged.sort(
            (a, b) =>
              new Date(b.parked_at).getTime() -
              new Date(a.parked_at).getTime(),
          );

          set({
            rows: merged,
            loading: false,
            lastSyncedAt: Date.now(),
          });

          // Side effect: retry any failed POSTs.
          for (const row of merged) {
            if (row.status === 'failed' || (row.status === 'pending' && !remoteIds.has(row.id))) {
              void retryPark(row);
            }
          }
        } catch {
          // Soft fail — keep whatever we had locally.
          set({ loading: false });
        }
      },

      async park(input) {
        if (!api) throw new Error('parkedOrders API not initialized');

        // Optimistic insert: show the card immediately with a local UUID.
        const idempotencyKey = generateUuid();
        const localId = `pending:${idempotencyKey}`;
        const parkedAt = new Date().toISOString();

        const optimistic: ParkedOrderRow = {
          id: localId,
          customer_label: input.customerLabel,
          item_count: input.itemCount,
          total_value: input.totalValue,
          note: input.note ?? null,
          parked_at: parkedAt,
          status: 'pending',
          snapshot: {
            ...input.snapshot,
            parked_at_client: parkedAt,
          },
          idempotencyKey,
        };

        set({ rows: [optimistic, ...get().rows] });

        // Fire the POST. On success, swap pending→synced with the server id.
        try {
          const result = await api.create({
            idempotency_key: idempotencyKey,
            snapshot: optimistic.snapshot,
            note: input.note ?? null,
          });

          set({
            rows: get().rows.map((row) =>
              row.id === localId
                ? {
                    id: result.id,
                    customer_label: input.customerLabel,
                    item_count: input.itemCount,
                    total_value: input.totalValue,
                    note: input.note ?? null,
                    parked_at: result.parked_at,
                    status: 'synced',
                  }
                : row,
            ),
          });
        } catch {
          // Mark failed but keep the row visible — the cashier can still
          // recall it locally, and refresh() will retry the POST.
          //
          // The narrowing dance: we only want to flip pending→failed for
          // the row whose POST we just sent. Other rows (including any
          // already-synced ones) pass through unchanged. Spreading on a
          // non-pending row would lose the discriminant fields.
          set({
            rows: get().rows.map((row): ParkedOrderRow => {
              if (row.id !== localId) return row;
              if (row.status === 'synced') return row;
              return { ...row, status: 'failed' };
            }),
          });
        }
      },

      async recall(id) {
        const local = get().rows.find((r) => r.id === id);

        // For pending/failed rows, the snapshot is already in localStorage —
        // recall is offline-safe.
        if (local && local.status !== 'synced') {
          return local.snapshot;
        }

        // For synced rows, fetch the snapshot from the server.
        if (!api) return null;
        try {
          const full = await api.getById(id);
          return full.snapshot;
        } catch {
          return null;
        }
      },

      async remove(id) {
        // Remove from local view immediately.
        set({ rows: get().rows.filter((r) => r.id !== id) });

        if (!api) return;
        // Pending rows never made it to the server — nothing to delete.
        if (id.startsWith('pending:')) return;

        try {
          await api.deleteById(id);
        } catch {
          // 404 = already gone (parallel tab). Anything else: surfaced
          // by refresh() on next boot. We don't re-add the row here —
          // that would be confusing; the cashier saw it disappear.
        }
      },

      async consume(id) {
        // Same as remove() but semantically distinct — used by the
        // Recall+Charge success path so it can be instrumented separately
        // from the trash-icon path if we want analytics later.
        await get().remove(id);
      },
    }),
    {
      name: 'matix-pos-parked-orders',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        rows: state.rows,
        lastSyncedAt: state.lastSyncedAt,
      }),
    },
  ),
);

// ── Helpers ──────────────────────────────────────────────────────────────

async function retryPark(row: ParkedOrderRow): Promise<void> {
  if (!api) return;
  if (row.status === 'synced') return;
  try {
    const result = await api.create({
      idempotency_key: row.idempotencyKey,
      snapshot: row.snapshot,
      note: row.note,
    });
    useParkedOrdersStore.setState({
      rows: useParkedOrdersStore.getState().rows.map((r) =>
        r.id === row.id
          ? {
              id: result.id,
              customer_label: row.customer_label,
              item_count: row.item_count,
              total_value: row.total_value,
              note: row.note,
              parked_at: result.parked_at,
              status: 'synced',
            }
          : r,
      ),
    });
  } catch {
    // Remain failed; refresh() will try again next time.
  }
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
